import { useEffect } from 'react'

import { LoadDataFromParams } from '../../Utils/url-helpers'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { getLocaleFromUrlOrQuery } from '../../Utils/common'
import { mapVehicleCodeToResidentSet, showResidentVehicleToggle } from '../../Utils/vehicles'

import { SelectedVehicle, UseVehiclesResult } from './vehicle-types'

function useInitPassengersFromQueryParams() {
  const { dispatch, state } = useTravelSearchContext()
  const { queryData } = LoadDataFromParams()

  useEffect(() => {
    if (state.selectedVehicle && !state.selectedPassengers) {
      dispatch({
        passengers: {
          adults: queryData.adults ?? 1,
          children: queryData.childrenAges?.length ?? 0,
          childrenAges: queryData.childrenAges ?? [],
          pets: queryData.pets ?? 0,
        },
        type: 'SET_PASSENGERS',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedPassengers, state.selectedVehicle, dispatch, queryData.adults, queryData.childrenAges?.length, queryData.pets])
}

function useEnforcePassengerLimits(matchedVehicle: any) {
  const { dispatch, state } = useTravelSearchContext()

  useEffect(() => {
    if (!matchedVehicle || !state.selectedPassengers) return
    const maxPassengers = matchedVehicle.maxNumberOfPassengers ?? 9
    const { children, childrenAges, pets } = state.selectedPassengers
    const adults = state.selectedPassengers.adults ?? 1
    const totalPassengers = adults + children
    if (totalPassengers > maxPassengers) {
      const adjustedAdults = Math.min(adults, maxPassengers)
      const adjustedChildren = Math.max(0, maxPassengers - adjustedAdults)
      const adjustedChildrenAges = childrenAges.slice(0, adjustedChildren)

      // Only dispatch if the values are actually different
      if (
        adjustedAdults !== state.selectedPassengers.adults ||
        adjustedChildren !== state.selectedPassengers.children ||
        JSON.stringify(adjustedChildrenAges) !== JSON.stringify(state.selectedPassengers.childrenAges)
      ) {
        dispatch({
          passengers: {
            adults: adjustedAdults,
            children: adjustedChildren,
            childrenAges: adjustedChildrenAges,
            pets: pets ?? 0,
          },
          type: 'SET_PASSENGERS',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    matchedVehicle?.maxNumberOfPassengers,
    state.selectedPassengers?.adults,
    state.selectedPassengers?.children,
    state.selectedPassengers?.childrenAges,
    state.selectedPassengers?.pets,
    dispatch,
  ])
}

export function useVehicles(): UseVehiclesResult {
  const { dispatch, state } = useTravelSearchContext()
  const vehicleTypesData = state.vehicleApiData
  const routesApiData = state.routesApiData
  const { queryData } = LoadDataFromParams()

  const routeCode = state.selectedRoute?.routeCode ?? ''
  const locale = getLocaleFromUrlOrQuery()
  const residentSwitchCheckboxVisible = showResidentVehicleToggle(routeCode, locale)

  const rawVehicleCode =
    state.selectedVehicle?.code ??
    queryData.vehicleCode ??
    (routesApiData?.defaultToNoVehicle ? 'NCAR' : (vehicleTypesData?.defaultVehicleCode ?? 'NCAR'))
  // RES vehicle types are being phased out; normalize any stored/queried code to its normal counterpart.
  const vehicleCode = rawVehicleCode !== 'NCAR' ? mapVehicleCodeToResidentSet(rawVehicleCode, false) : rawVehicleCode
  const matchedVehicle = vehicleTypesData?.vehicleTypes?.find((v) => v.code === vehicleCode)
  const isVehicleValid = (vehicleCode === 'NCAR' && !vehicleTypesData?.isVehicleMandatory) || !!matchedVehicle
  const defaultVehicleCode =
    routesApiData?.defaultToNoVehicle && !vehicleTypesData?.isVehicleMandatory ? 'NCAR' : (vehicleTypesData?.defaultVehicleCode ?? 'NCAR')
  const validatedVehicleCode = isVehicleValid ? vehicleCode : defaultVehicleCode

  // Validate trailer
  const availableTrailers = vehicleTypesData?.vehicleTypes?.find((x: any) => x.code === validatedVehicleCode)?.availableTrailers || []
  let validatedTrailer = state.selectedVehicle?.trailer
  if (state.selectedVehicle?.trailer) {
    const currentTrailerCode = state.selectedVehicle.trailer.code
    // RES vehicle types are being phased out; keep the trailer if it exists under its current
    // or normalized code, otherwise remove it (some trailers are not available in the normal set).
    const mappedTrailerCode = mapVehicleCodeToResidentSet(currentTrailerCode, false)
    validatedTrailer =
      availableTrailers.find((x) => x.code === currentTrailerCode) ??
      availableTrailers.find((x) => x.code === mappedTrailerCode) ??
      undefined
  }

  useInitPassengersFromQueryParams()
  useEnforcePassengerLimits(matchedVehicle)

  const selectedPassengers = state.selectedPassengers ?? {
    adults: 1,
    children: 0,
    childrenAges: [],
    pets: 0,
  }

  const selectedVehicle: SelectedVehicle = {
    categoryCode: state.selectedVehicle?.categoryCode,
    code: validatedVehicleCode,
    count: validatedVehicleCode === 'NCAR' ? 0 : (state.selectedVehicle?.count ?? 1),
    trailer: validatedTrailer,
  }

  const isVehiclesLoading = !state.selectedRoute?.routeCode || !vehicleTypesData || !routesApiData || state.vehicleApiLoading

  // Sync selectedVehicle to global state if it differs
  useEffect(() => {
    if (isVehiclesLoading) return
    const current = state.selectedVehicle
    const expectedCount = validatedVehicleCode === 'NCAR' ? 0 : (state.selectedVehicle?.count ?? 1)
    // Only dispatch if any property actually changed
    if (
      !current ||
      current.code !== validatedVehicleCode ||
      current.trailer !== validatedTrailer ||
      current.count !== expectedCount ||
      current.categoryCode !== state.selectedVehicle?.categoryCode
    ) {
      // Prevent dispatch loop: don't dispatch if the new value is deep-equal to the current
      if (
        current &&
        current.code === selectedVehicle.code &&
        current.trailer === selectedVehicle.trailer &&
        current.count === selectedVehicle.count &&
        current.categoryCode === selectedVehicle.categoryCode
      ) {
        return
      }
      dispatch({
        type: 'SET_VEHICLE',
        vehicle: selectedVehicle,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isVehiclesLoading,
    validatedVehicleCode,
    validatedTrailer,
    state.selectedVehicle?.code,
    state.selectedVehicle?.trailer,
    state.selectedVehicle?.count,
    state.selectedVehicle?.categoryCode,
    selectedVehicle.code,
    selectedVehicle.trailer,
    selectedVehicle.count,
    selectedVehicle.categoryCode,
    dispatch,
  ])

  const petsAllowed =
    validatedVehicleCode === 'NCAR' ? (vehicleTypesData?.isPetsAllowedForFootPassenger ?? false) : (matchedVehicle?.petsAllowed ?? true)

  const totalPassengers = selectedPassengers.adults + selectedPassengers.children
  const maxPassengers = matchedVehicle?.maxNumberOfPassengers ?? 9

  return {
    isVehiclesLoading,
    maxNumberOfPassengers: maxPassengers,
    multipleVehicles: state.multipleVehiclesChecked,
    numberOfPassengers: totalPassengers,
    petsAllowed,
    residentCheckboxVisible: residentSwitchCheckboxVisible,
    selectedPassengers,
    selectedVehicle,
    vehicleApiData: vehicleTypesData ?? ({} as any),
  }
}
